import { DialogComponent } from '@theme/dialog';
import { escapeHtml } from '@theme/text-utils';

const DEFAULT_API_BASE_URL = `https://${window.location.host}/apps/jyapi`;
const SESSION_TOKEN_KEY = 'jy_styleboard_session_token';
const SESSION_TOKENS_KEY = 'jy_styleboard_session_tokens';
const LAST_STYLEBOARD_KEY = 'jy_last_styleboard_id'; // customer
const LAST_GUEST_STYLEBOARD_KEY = 'jy_last_styleboard_id_guest';
const CLAIM_SYNC_KEY = 'jy_styleboard_claim_synced';

/**
 * Gets the notification function, with fallback to window.notification
 * @returns {{ success: Function, error: Function, warn: Function } | null}
 */
function getNotification() {
  if (typeof window !== 'undefined') {
    const win = /** @type {any} */ (window);
    if (win.notification) {
      return win.notification;
    }
  }
  console.warn('Notification system not available, falling back to alert');
  return null;
}

/**
 * A custom element that manages the styleboard selector modal.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 * @property {HTMLElement} styleboardsList – The container for styleboards list.
 * @property {HTMLInputElement} newStyleboardName – The input for new styleboard name.
 * @property {HTMLButtonElement} cancelBtn – The cancel button.
 * @property {HTMLButtonElement} submitBtn – The submit button.
 * @property {HTMLButtonElement} closeButton – The close button.
 *
 * @extends {DialogComponent}
 */
export class StyleboardSelectorDialog extends DialogComponent {
  requiredRefs = ['dialog', 'styleboardsList', 'newStyleboardName', 'cancelBtn', 'submitBtn', 'closeButton', 'orDivider'];

  /**
   * Gets the API base URL from the data attribute.
   * @returns {string}
   */
  get apiBaseUrl() {
    return Theme.api?.baseUrl || DEFAULT_API_BASE_URL;
  }

  /**
   * Returns session token for guest users (from localStorage).
   * @returns {string | null}
   */
  static getSessionToken() {
    try {
      const direct = localStorage.getItem(SESSION_TOKEN_KEY);
      if (direct) return direct;
      const tokens = StyleboardSelectorDialog.getSessionTokens();
      return tokens[0] || null;
    } catch {
      return null;
    }
  }

  /**
   * Returns all guest session tokens.
   * @returns {string[]}
   */
  static getSessionTokens() {
    try {
      const raw = localStorage.getItem(SESSION_TOKENS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      const tokens = Array.isArray(parsed)
        ? parsed.filter((token) => typeof token === 'string' && token.length > 0)
        : [];
      if (tokens.length > 0) {
        return [...new Set(tokens)];
      }
      const single = localStorage.getItem(SESSION_TOKEN_KEY);
      return single ? [single] : [];
    } catch {
      const single = localStorage.getItem(SESSION_TOKEN_KEY);
      return single ? [single] : [];
    }
  }

  /**
   * Saves all guest session tokens.
   * @param {string[]} tokens
   */
  static setSessionTokens(tokens) {
    try {
      const normalized = [...new Set(tokens.filter((token) => typeof token === 'string' && token.length > 0))];
      localStorage.setItem(SESSION_TOKENS_KEY, JSON.stringify(normalized));
      if (normalized[0]) {
        localStorage.setItem(SESSION_TOKEN_KEY, normalized[0]);
      }
    } catch {
      console.warn('Failed to save session token list to localStorage');
    }
  }

  /**
   * Clears all guest styleboard tokens/ids from storage.
   */
  static clearSessionTokens() {
    try {
      localStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(SESSION_TOKENS_KEY);
      localStorage.removeItem('styleboard_session_token');
      localStorage.removeItem(LAST_GUEST_STYLEBOARD_KEY);
    } catch {
      console.warn('Failed to clear session tokens from localStorage');
    }
  }

  /**
   * Checks whether the current page context has a logged-in customer.
   * @returns {boolean}
   */
  static isLoggedInCustomer() {
    try {
      const el = document.querySelector('styleboard-selector-dialog');
      return el?.getAttribute('data-customer-logged-in') === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Saves session token for guest users.
   * @param {string} token
   */
  static setSessionToken(token) {
    StyleboardSelectorDialog.addSessionToken(token);
  }

  /**
   * Adds a guest session token while keeping existing tokens.
   * Latest token is moved to first position.
   * @param {string} token
   */
  static addSessionToken(token) {
    if (!token) return;
    const existing = StyleboardSelectorDialog.getSessionTokens();
    const merged = [token, ...existing.filter((item) => item !== token)];
    StyleboardSelectorDialog.setSessionTokens(merged);
  }

  /**
   * Appends session token parameters to URL.
   * Adds both legacy single token and array params for backend compatibility.
   * @param {URL} url
   */
  static applySessionTokensToUrl(url) {
    const tokens = StyleboardSelectorDialog.getSessionTokens();
    if (!tokens.length) return;
    if (url.pathname.includes('/retail/styleboards-list')) {
      tokens.forEach((token) => {
        url.searchParams.append('ids[]', token);
      });
      return;
    }
    const primaryToken = tokens[0];
    if (primaryToken) {
      url.searchParams.set('session_token', primaryToken);
    }
  }

  /**
   * Returns last used styleboard id.
   * @returns {string | null}
   */
  static getLastStyleboardId() {
    try {
      const isLoggedIn = StyleboardSelectorDialog.isLoggedInCustomer();
      if (isLoggedIn) {
        return localStorage.getItem(LAST_STYLEBOARD_KEY) || null;
      }

      const guestLast = localStorage.getItem(LAST_GUEST_STYLEBOARD_KEY) || null;
      if (guestLast) {
        const tokens = StyleboardSelectorDialog.getSessionTokens();
        return tokens.includes(guestLast) ? guestLast : null;
      }

      // Backward compatible fallback: if old key contains a guest token, allow it.
      const legacy = localStorage.getItem(LAST_STYLEBOARD_KEY) || null;
      if (!legacy) return null;
      const tokens = StyleboardSelectorDialog.getSessionTokens();
      return tokens.includes(legacy) ? legacy : null;
    } catch {
      return null;
    }
  }

  /**
   * Saves last used styleboard id.
   * @param {string} id
   */
  static setLastStyleboardId(id) {
    try {
      const isLoggedIn = StyleboardSelectorDialog.isLoggedInCustomer();
      if (isLoggedIn) {
        localStorage.setItem(LAST_STYLEBOARD_KEY, id);
        return;
      }

      // Guest context: keep separate so we don't leak customer styleboard ids after logout.
      StyleboardSelectorDialog.addSessionToken(id);
      localStorage.setItem(LAST_GUEST_STYLEBOARD_KEY, id);
    } catch {
      console.warn('Failed to save last styleboard ID');
    }
  }

  /**
   * Clears last used styleboard id.
   */
  static clearLastStyleboardId() {
    try {
      const isLoggedIn = StyleboardSelectorDialog.isLoggedInCustomer();
      localStorage.removeItem(isLoggedIn ? LAST_STYLEBOARD_KEY : LAST_GUEST_STYLEBOARD_KEY);
    } catch {
      console.warn('Failed to clear last styleboard ID');
    }
  }

  /**
   * Clears guest last used styleboard id (always).
   */
  static clearGuestLastStyleboardId() {
    try {
      localStorage.removeItem(LAST_GUEST_STYLEBOARD_KEY);
    } catch {
      console.warn('Failed to clear guest last styleboard ID');
    }
  }

  /**
   * The product data to add to styleboard.
   * @type {{ style: string, color?: string | null, type: string, productId?: string, productHandle?: string, variantId?: string | null } | null}
   */
  #product = null;

  /**
   * The selected styleboard ID.
   * @type {string | null}
   */
  #selectedStyleBoardId = null;

  /**
   * The list of styleboards.
   * @type {Array<{ id: string, name?: string }>}
   */
  #styleBoards = [];

  connectedCallback() {
    super.connectedCallback();
    this.refs.newStyleboardName.addEventListener('keypress', this.#handleKeyPress);
    this.refs.newStyleboardName.addEventListener('input', this.#handleInput);
    void this.#claimGuestStyleboardsIfNeeded().catch((error) => {
      console.error('Failed to claim guest styleboards:', error);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.newStyleboardName.removeEventListener('keypress', this.#handleKeyPress);
    this.refs.newStyleboardName.removeEventListener('input', this.#handleInput);
  }

  /**
   * Shows the dialog with product data.
   * @param {{ style: string, color?: string | null, type: string, productId?: string, productHandle?: string, variantId?: string | null }} product - Product data
   */
  showWithProduct(product) {
    if (!product || !product.style) {
      const notify = getNotification();
      if (notify && typeof notify.error === 'function') {
        notify.error('Invalid product data', 'Style number is required to add product to styleboard');
      } else {
        alert('Invalid product data: Style number is required');
      }
      return;
    }

    if (!product.type) {
      const notify = getNotification();
      if (notify && typeof notify.warn === 'function') {
        notify.warn('Product type missing', 'Defaulting to ecom. Please select a variant.');
      }
      product.type = 'ecom';
    }

    this.#product = product;
    this.#selectedStyleBoardId = null;
    if (this.refs.newStyleboardName) {
      this.refs.newStyleboardName.value = '';
    }

    const titleEl = this.querySelector('h2');
    if (titleEl) {
      titleEl.textContent = titleEl.getAttribute('data-original-title') || 'Add to Style Board';
    }

    const listEl = /** @type {HTMLElement | null} */ (this.refs.styleboardsList);
    if (listEl) listEl.style.display = '';
    const orDividerEl = /** @type {HTMLElement | null} */ (this.refs.orDivider);
    if (orDividerEl) orDividerEl.style.display = '';
    const submitBtn = /** @type {HTMLButtonElement | null} */ (this.refs.submitBtn);
    if (submitBtn) submitBtn.textContent = 'Add';

    this.showDialog();
    this.#loadStyleBoards();
  }

  /**
   * Opens the dialog in create-only mode (no product add).
   */
  showCreateMode = () => {
    this.#product = null;
    this.#selectedStyleBoardId = null;
    if (this.refs.newStyleboardName) {
      this.refs.newStyleboardName.value = '';
    }

    const titleEl = this.querySelector('h2');
    if (titleEl) {
      titleEl.textContent = 'Create Style Board';
    }

    const listEl = /** @type {HTMLElement | null} */ (this.refs.styleboardsList);
    if (listEl) listEl.style.display = 'none';
    const orDividerEl = /** @type {HTMLElement | null} */ (this.refs.orDivider);
    if (orDividerEl) orDividerEl.style.display = 'none';
    const submitBtn = /** @type {HTMLButtonElement | null} */ (this.refs.submitBtn);
    if (submitBtn) submitBtn.textContent = 'Create';

    this.showDialog();
  };

  /**
   * Handles key press on the new styleboard name input.
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleSubmit();
    }
  };

  /**
   * Handles input on the new styleboard name field.
   */
  #handleInput = () => {
    if (this.refs.newStyleboardName.value.trim()) {
      this.#selectedStyleBoardId = null;
      this.refs.styleboardsList.querySelectorAll('input[type="radio"]').forEach((radio) => {
        /** @type {HTMLInputElement} */ (radio).checked = false;
      });
    }
  };

  /**
   * Fetches CSRF token from server. Always fetches to ensure a valid token (no cookie shortcut).
   * @returns {Promise<string | null>} The CSRF token or null if not available
   */
  async #getCsrfToken() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/csrf-token`, {
        method: 'GET',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json().catch(() => ({}));
      const token = data?.token ?? data?.csrf_token ?? data?.csrfToken ?? data?._token ?? null;
      return token || null;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return null;
    }
  }

  /**
   * Claims guest styleboards for logged-in customer and clears guest IDs on success.
   * Runs once per session.
   */
  async #claimGuestStyleboardsIfNeeded() {
    const isLoggedInCustomer = this.getAttribute('data-customer-logged-in') === 'true';
    if (!isLoggedInCustomer) {
      try {
        sessionStorage.removeItem(CLAIM_SYNC_KEY);
      } catch {
        // Ignore storage access errors.
      }
      return;
    }

    try {
      if (sessionStorage.getItem(CLAIM_SYNC_KEY) === '1') {
        return;
      }
    } catch {
      // Ignore storage access errors.
    }

    const ids = StyleboardSelectorDialog.getSessionTokens();
    if (!ids.length) {
      try {
        sessionStorage.setItem(CLAIM_SYNC_KEY, '1');
      } catch {
        // Ignore storage access errors.
      }
      return;
    }

    const csrfToken = await this.#getCsrfToken();
    const response = await fetch(`${this.apiBaseUrl}/retail/styleboards/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        ids,
        _token: csrfToken,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to claim guest styleboards');
    }

    StyleboardSelectorDialog.clearSessionTokens();
    StyleboardSelectorDialog.clearGuestLastStyleboardId();
    try {
      sessionStorage.setItem(CLAIM_SYNC_KEY, '1');
    } catch {
      // Ignore storage access errors.
    }
  }

  /**
   * Fetches styleboards from the API.
   * @returns {Promise<Array<Object>>} Array of styleboards
   */
  async #fetchStyleBoards() {
    try {
      const response = await fetch(this.#buildApiUrl('/retail/styleboards-list'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const notify = getNotification();
        if (notify && typeof notify.error === 'function') {
          notify.error('Failed to load styleboards', errorData.message || 'Please try again later');
        }
        throw new Error(errorData.message || 'Failed to fetch styleboards');
      }

      const data = await response.json();
      return /** @type {Array<{ id: string, name?: string }>} */ (data.data?.styleboards || []);
    } catch (error) {
      console.error('Error fetching styleboards:', error);
      return [];
    }
  }

  /**
   * Creates a new styleboard.
   * @param {string} name - Styleboard name
   * @returns {Promise<string>} The created styleboard ID
   */
  async #createStyleBoard(name, isRetry = false) {
    try {
      const csrfToken = await this.#getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      const body = { name, _token: csrfToken };
      const response = await fetch(this.#buildApiUrl('/retail/styleboards'), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.status === 419 && !isRetry) {
        const freshToken = await this.#getCsrfToken();
        if (freshToken) {
          return this.#createStyleBoard(name, true);
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to create styleboard';
        const notify = getNotification();
        if (notify && typeof notify.error === 'function') {
          notify.error('Failed to create styleboard', errorMessage);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const responseToken = data.data?.session_token || data.data?.id;
      if (typeof responseToken === 'string' && responseToken.length > 0) {
        StyleboardSelectorDialog.setSessionToken(responseToken);
      }
      return data.data?.styleboard?.id;
    } catch (error) {
      console.error('Error creating styleboard:', error);
      throw error;
    }
  }

  /**
   * Adds a product to a styleboard.
   * @param {string} styleBoardId - The styleboard ID
   * @param {{ style: string, color?: string | null, type: string, note?: string | null }} product - Product data
   * @returns {Promise<Object>} Response data
   */
  async #addProductToStyleBoard(styleBoardId, product, isRetry = false) {
    console.log(product)
    try {
      const csrfToken = await this.#getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      const body = {
        style: product.style,
        color: product.color || null,
        type: product.type,
        note: product.note || null,
        _token: csrfToken,
      };
      const response = await fetch(this.#buildApiUrl(`/retail/styleboards/${styleBoardId}/items`), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.status === 419 && !isRetry) {
        const freshToken = await this.#getCsrfToken();
        if (freshToken) {
          return this.#addProductToStyleBoard(styleBoardId, product, true);
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to add product to styleboard';
        const notify = getNotification();
        if (notify && typeof notify.error === 'function') {
          notify.error('Failed to add product to styleboard', errorMessage);
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding product to styleboard:', error);
      if (error instanceof Error && error.message) {
        const notify = getNotification();
        if (notify && typeof notify.error === 'function') {
          notify.error('Failed to add product to styleboard', error.message);
        }
      }
      throw error;
    }
  }

  /**
   * Loads styleboards and populates the list.
   */
  async #loadStyleBoards() {
    const { styleboardsList } = this.refs;
    styleboardsList.innerHTML = '<p>Loading styleboards...</p>';

    this.#styleBoards = await this.#fetchStyleBoards();

    if (this.#styleBoards.length === 0) {
      styleboardsList.innerHTML = '<p>No styleboards found. Create a new one below.</p>';
      return;
    }

    styleboardsList.innerHTML = this.#styleBoards
      .map(
        (sb) => {
          const safeId = escapeHtml(String(sb.id ?? ''));
          const safeName = escapeHtml(sb.name || 'Unnamed Style Board');
          return `
        <label class="flex items-center gap-2 p-3 border border-foreground/10 rounded cursor-pointer transition-colors hover:bg-primary/4" for="styleboard-${safeId}">
          <input type="radio" class="h-4.5 w-4.5 accent-white" name="styleboard" value="${safeId}" id="styleboard-${safeId}" />
          <span class="text-primary text-sm">
            ${safeName}
          </span>
        </label>
      `;
        }
      )
      .join('');

    styleboardsList.querySelectorAll('input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', (e) => {
        const target = /** @type {HTMLInputElement | null} */ (e.target);
        if (target && target.checked) {
          this.#selectedStyleBoardId = target.value;
          this.refs.newStyleboardName.value = '';
        }
      });
    });
  }

  /**
   * Builds API URL with session token for guest users.
   * @param {string} endpoint
   * @returns {string}
   */
  #buildApiUrl(endpoint) {
    const url = new URL(`${this.apiBaseUrl}${endpoint}`, /^https?:\/\//i.test(this.apiBaseUrl) ? undefined : window.location.origin);
    StyleboardSelectorDialog.applySessionTokensToUrl(url);
    return url.toString();
  }

  /**
   * Handles form submission.
   * This method is called from the on:click attribute in the Liquid template.
   */
  handleSubmit = async () => {
    const newName = this.refs.newStyleboardName.value.trim();

    if (!this.#product) {
      if (!newName) {
        alert('Please enter a name for the new styleboard.');
        return;
      }

      this.refs.submitBtn.disabled = true;
      this.refs.submitBtn.textContent = 'Creating...';
      try {
        const styleBoardId = await this.#createStyleBoard(newName);
        if (!styleBoardId) {
          throw new Error('Failed to create styleboard');
        }
        const notify = getNotification();
        if (notify && typeof notify.success === 'function') {
          notify.success('Styleboard created successfully!');
        } else {
          alert('Styleboard created successfully!');
        }
        this.closeDialog();
        window.location.href = `/apps/jyapi/retail/styleboards/${styleBoardId}`;
      } catch (error) {
        const notify = getNotification();
        if (notify && typeof notify.error === 'function') {
          notify.error('Failed to create styleboard', error?.message || 'An error occurred');
        } else {
          alert(error?.message || 'Failed to create styleboard');
        }
      } finally {
        this.refs.submitBtn.disabled = false;
        this.refs.submitBtn.textContent = 'Create';
      }
      return;
    }

    if (!this.#selectedStyleBoardId && !newName) {
      alert('Please select a styleboard or enter a name for a new one.');
      return;
    }

    this.refs.submitBtn.disabled = true;
    this.refs.submitBtn.textContent = 'Adding...';

    try {
      let styleBoardId = this.#selectedStyleBoardId;

      if (newName && !styleBoardId) {
        styleBoardId = await this.#createStyleBoard(newName);
      }

      if (!styleBoardId) {
        throw new Error('Styleboard ID is required');
      }

      await this.#addProductToStyleBoard(styleBoardId, this.#product);
      StyleboardSelectorDialog.setLastStyleboardId(styleBoardId);

      const notify = getNotification();
      if (notify && typeof notify.success === 'function') {
        notify.success('Product added to styleboard successfully!');
      } else {
        console.warn('Notification not available, using alert');
        alert('Product added to styleboard successfully!');
      }
      this.closeDialog();

      document.dispatchEvent(
        new CustomEvent('product-added-to-styleboard', {
          detail: { styleBoardId, product: this.#product },
        })
      );
    } catch (error) {
      const notify = getNotification();
      if (notify && typeof notify.error === 'function') {
        notify.error('Failed to add product to styleboard', error.message || 'An error occurred');
      } else {
        console.warn('Notification not available, using alert');
        alert(error.message || 'Failed to add product to styleboard');
      }
      if (this.refs.submitBtn) {
        this.refs.submitBtn.disabled = false;
        this.refs.submitBtn.textContent = 'Add';
        this.refs.submitBtn.innerHTML = 'Add';
      }
    } finally {
      if (this.refs.submitBtn) {
        this.refs.submitBtn.disabled = false;
        this.refs.submitBtn.textContent = 'Add';
      }
    }
  };
}

if (!customElements.get('styleboard-selector-dialog')) {
  customElements.define('styleboard-selector-dialog', /** @type {typeof HTMLElement} */(/** @type {any} */ (StyleboardSelectorDialog)));
}
